import { useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { clsx } from '../../ds/clsx';
import {
  Avatar,
  Banner,
  Button,
  Eyebrow,
  FilterChip,
  Pill,
  Segmented,
  TableSkeleton,
  useToast,
} from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import {
  approveLabel,
  decisionLabel,
  typePillVariant,
  useApprovals,
  useDecideApproval,
  type ApprovalItem,
  type ApprovalListStatus,
  type ApprovalType,
} from './api';
import { ApprovalDialog } from './ApprovalDialog';
import styles from './ApprovalsPage.module.css';

/** „19 Tage" waits are highlighted from a week on (Backoffice 09). */
function waitingTone(label: string): boolean {
  const match = /^(\d+) Tage$/.exec(label);
  return match !== null && Number(match[1]) >= 7;
}

/**
 * `/tasks/approvals` — Backoffice 09/10: open approvals with type filter, requester, waiting time and the
 * decision buttons; „Erledigt" lists the decided ones; the empty state carries „Zuletzt erledigt".
 * `?approval=<id>` opens the detail dialog.
 */
export function ApprovalsPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const workspace = useWorkspace();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<ApprovalListStatus>('open');
  const [type, setType] = useState<ApprovalType | null>(null);
  const approvals = useApprovals(status);
  const decide = useDecideApproval();
  const approvalId = params.get('approval');

  function openDetail(id: string | null) {
    const next = new URLSearchParams(params);
    if (id === null) next.delete('approval');
    else next.set('approval', id);
    setParams(next, { replace: id === null });
  }

  function quickDecide(item: ApprovalItem, decision: 'approved') {
    decide.mutate(
      { id: item.id, body: { decision } },
      {
        onSuccess: (detail) =>
          toast({ message: `${detail.typeLabel} ${decisionLabel(detail.status)}: ${detail.title}` }),
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) openDetail(item.id);
          else toast({ message: errorMessage(error) });
        },
      },
    );
  }

  const data = approvals.data;
  const items = (data?.items ?? []).filter((item) => type === null || item.type === type);
  const openCount = data?.counts.open ?? 0;

  function rowKey(event: KeyboardEvent, id: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDetail(id);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <div className={styles.crumb}>Tasks</div>
          <h1 className={styles.title}>Freigaben</h1>
          {status === 'open' && openCount > 0 ? (
            <div className={styles.subtitle}>
              {openCount === 1 ? '1 wartet auf Sie' : `${openCount} warten auf Sie`}
            </div>
          ) : null}
        </div>
        <div className={styles.headerEnd}>
          <Segmented
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setType(null);
            }}
            items={[
              { value: 'open', label: `Wartend · ${data?.counts.open ?? 0}` },
              { value: 'decided', label: 'Erledigt' },
            ]}
          />
        </div>
      </div>

      {status === 'open' && data && data.counts.byType.length > 0 ? (
        <div className={styles.chips}>
          <FilterChip
            label="Alle"
            count={data.counts.open}
            active={type === null}
            onClick={() => setType(null)}
          />
          {data.counts.byType.map((entry) => (
            <FilterChip
              key={entry.type}
              label={entry.label}
              count={entry.count}
              active={type === entry.type}
              onClick={() => setType(type === entry.type ? null : entry.type)}
            />
          ))}
        </div>
      ) : null}

      {approvals.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void approvals.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(approvals.error)}
        </Banner>
      ) : null}

      {approvals.isPending ? (
        <div className={styles.card}>
          <TableSkeleton
            columns={['130px', 'minmax(0,1.5fr)', '170px', '150px', '240px']}
            rows={4}
            headerTone="plain"
          />
        </div>
      ) : null}

      {data && items.length === 0 ? (
        <div className={clsx(styles.card, styles.emptyCard)}>
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>
              {status === 'open' ? 'Keine offenen Freigaben.' : 'Noch keine erledigten Freigaben.'}
            </div>
            <div className={styles.emptyText}>
              {status === 'open'
                ? 'Neue Anträge erscheinen hier und als Task in Ihrer Queue.'
                : 'Entschiedene Anträge erscheinen hier.'}
            </div>
            {status === 'open' && data.recent.length > 0 ? (
              <div className={styles.recent}>
                <div className={styles.recentLabel}>Zuletzt erledigt</div>
                {data.recent.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={styles.recentRow}
                    onClick={() => openDetail(entry.id)}
                  >
                    <span className={styles.recentTitle}>{entry.title}</span>
                    <span className={styles.recentValue}>
                      {entry.decisionLabel} · {entry.decidedLabel}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {data && items.length > 0 ? (
        <div className={clsx(styles.card, styles.tableCard)}>
          <div className={styles.table}>
            {mobile ? null : (
              <div className={clsx(styles.gridRow, styles.head)} role="presentation">
                <Eyebrow>Art</Eyebrow>
                <Eyebrow>Vorgang</Eyebrow>
                <Eyebrow>Beantragt von</Eyebrow>
                <Eyebrow>{status === 'open' ? 'Wartet seit' : 'Entschieden'}</Eyebrow>
                <span />
              </div>
            )}
            {items.map((item) => {
              const own = item.requestedBy?.id === workspace.user.id;
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  className={clsx(styles.gridRow, styles.row)}
                  onClick={() => openDetail(item.id)}
                  onKeyDown={(event) => rowKey(event, item.id)}
                >
                  <span>
                    <Pill variant={typePillVariant(item.type)}>{item.typeLabel}</Pill>
                  </span>
                  <span className={styles.subject}>
                    <span className={styles.subjectTitle}>{item.title}</span>
                    {item.subtitle ? <span className={styles.subjectSub}>{item.subtitle}</span> : null}
                  </span>
                  <span className={styles.requester}>
                    {item.requestedBy ? (
                      <>
                        <Avatar
                          initials={item.requestedBy.initials}
                          name={item.requestedBy.name}
                          size={26}
                          tone="navy"
                          className={styles.requesterAvatar}
                        />
                        <span className={styles.requesterName}>{item.requestedBy.name}</span>
                      </>
                    ) : (
                      <span className={styles.requesterName}>System</span>
                    )}
                  </span>
                  <span
                    className={clsx(
                      styles.waiting,
                      status === 'open' && waitingTone(item.waitingLabel) && styles.waitingLong,
                    )}
                  >
                    {status === 'open' ? item.waitingLabel : decisionLabel(item.status)}
                  </span>
                  <span className={styles.actions} onClick={(event) => event.stopPropagation()}>
                    {status === 'open' && item.decidable ? (
                      <>
                        <Button variant="secondary" size="small" onClick={() => openDetail(item.id)}>
                          {item.type === 'property' ? 'Ansehen' : 'Ablehnen'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="small"
                          className={styles.approve}
                          loading={decide.isPending && decide.variables?.id === item.id}
                          onClick={() => quickDecide(item, 'approved')}
                        >
                          {approveLabel(item.type)}
                        </Button>
                      </>
                    ) : status === 'open' &&
                      (item.type === 'reservation' || item.type === 'release') &&
                      item.refs.reservationId ? (
                      // Reservation and release approvals are decided in the dialog (Reservierung 08/09).
                      <>
                        <Button variant="secondary" size="small" onClick={() => openDetail(item.id)}>
                          Ablehnen
                        </Button>
                        <Button
                          variant="secondary"
                          size="small"
                          className={styles.approve}
                          onClick={() => openDetail(item.id)}
                        >
                          {approveLabel(item.type)}
                        </Button>
                      </>
                    ) : status === 'open' ? (
                      <span className={styles.blocked}>
                        {item.type === 'duplicate' && item.refs.duplicateCandidateId ? (
                          <Link
                            to={`/tasks/duplicates/${item.refs.duplicateCandidateId}`}
                            className={styles.blockedLink}
                          >
                            Zusammenführung öffnen
                          </Link>
                        ) : (
                          (item.blockedReason ?? (own ? 'Ihr Antrag' : ''))
                        )}
                      </span>
                    ) : (
                      <Button variant="secondary" size="small" onClick={() => openDetail(item.id)}>
                        Ansehen
                      </Button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {data ? (
        <div className={styles.footnote}>
          Sie sehen Freigaben, für die Sie das Recht haben. Anträge erscheinen zusätzlich als Task in Ihrer
          Queue.
        </div>
      ) : null}

      <ApprovalDialog
        approvalId={approvalId}
        onClose={() => openDetail(null)}
        onDecided={(detail) => {
          toast({ message: `${detail.typeLabel} ${decisionLabel(detail.status)}: ${detail.title}` });
          openDetail(null);
          if (detail.status !== 'open' && status !== 'open') void navigate('/tasks/approvals');
        }}
      />
    </div>
  );
}
